import React, { useEffect, useState } from 'react';
import { X, Search, UserMinus } from 'lucide-react';
import { toast } from 'react-toastify';
import useOrgHierarchyStore from '@/stores/SuperAdminStore/OrgHierarchyStore';
import { HierarchyNode, SearchHit, searchUsers } from '@/services/PagesServices/OrgHierarchyService';

interface ReassignManagerModalProps {
  node: HierarchyNode;
  onClose: () => void;
}

/**
 * Pick a new manager for `node` (or clear it). The backend validates
 * self-reference and reporting cycles and returns a 409 on conflict.
 */
const ReassignManagerModal: React.FC<ReassignManagerModalProps> = ({ node, onClose }) => {
  const { reassignManager, saving } = useOrgHierarchyStore();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchHit[]>([]);
  const [selected, setSelected] = useState<SearchHit | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  // Debounced search.
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    let active = true;
    setSearching(true);
    const t = setTimeout(async () => {
      const res = await searchUsers(query);
      if (!active) return;
      // Exclude the node itself from candidate managers.
      setResults((res.data || []).filter((h) => h.uuid !== node.uuid));
      setSearching(false);
    }, 250);
    return () => { active = false; clearTimeout(t); };
  }, [query, node.uuid]);

  const submit = async (managerUuid: string | null) => {
    const res = await reassignManager(node.uuid, managerUuid);
    if (res.success) { toast.success(res.message); onClose(); }
    else toast.error(res.message);
  };

  return (
    <div className="s-admin-org-modal-overlay" onClick={onClose}>
      <div className="s-admin-org-modal" onClick={(e) => e.stopPropagation()}>
        <div className="s-admin-org-modal-header">
          <h3>Reassign manager</h3>
          <button className="s-admin-org-icon-btn" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        <div className="s-admin-org-modal-body">
          <p className="s-admin-org-modal-sub">
            Set who <strong>{node.name}</strong> reports to.
          </p>

          <div className="s-admin-org-search">
            <Search size={16} />
            <input
              autoFocus
              placeholder="Search for a manager by name or email"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
            />
          </div>

          {searching && <div className="s-admin-org-search-note">Searching…</div>}

          {results.length > 0 && (
            <ul className="s-admin-org-results">
              {results.map((hit) => (
                <li
                  key={hit.uuid}
                  className={`s-admin-org-result ${selected?.uuid === hit.uuid ? 'selected' : ''}`}
                  onClick={() => setSelected(hit)}
                >
                  <div className="s-admin-org-result-avatar">{hit.name.charAt(0).toUpperCase()}</div>
                  <div>
                    <div className="s-admin-org-result-name">{hit.name}</div>
                    <div className="s-admin-org-result-meta">{hit.email} · {hit.roleLabel}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="s-admin-org-modal-footer">
          <button
            className="s-admin-org-btn-ghost"
            onClick={() => submit(null)}
            disabled={saving || !node.managerUuid}
            title={node.managerUuid ? 'Remove current manager' : 'No manager set'}
          >
            <UserMinus size={14} /> Clear manager
          </button>
          <div className="s-admin-org-footer-right">
            <button className="s-admin-org-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button
              className="s-admin-org-btn-primary"
              onClick={() => selected && submit(selected.uuid)}
              disabled={saving || !selected}
            >
              {saving ? 'Saving…' : 'Assign manager'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReassignManagerModal;
